import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { apiUrl, SOCKET_URL } from '../../config/api';
import { io, Socket, ManagerOptions, SocketOptions } from 'socket.io-client';
import { Send, Smile, Check, CheckCheck, Clock, File, Download } from 'lucide-react';
import { FileUpload } from './FileUpload';

interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  body: string;
  status: 'SENT' | 'DELIVERED' | 'READ';
  createdAt: string;
  updatedAt: string;
  attachments?: Attachment[];
}

interface Attachment {
  id: string;
  name: string;
  url: string;
  mimeType: string;
  size: number;
}

interface Tailor {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface ChatInterfaceProps {
  conversationId: string;
  tailor: Tailor;
  currentUserId: number;
}

interface TypingIndicator {
  userId: string;
  isTyping: boolean;
}

const sortMessagesAscending = (messages: Message[]) =>
  [...messages].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

const normalizeMessage = (
  rawMessage: any,
  fallbackSenderId: number,
  fallbackConversationId: string
): Message => {
  const createdAt = rawMessage?.createdAt ?? new Date().toISOString();

  return {
    id: rawMessage?.id ?? `${fallbackConversationId}-${Date.now()}`,
    conversationId: rawMessage?.conversationId ?? fallbackConversationId,
    senderId: String(rawMessage?.senderId ?? fallbackSenderId),
    body: rawMessage?.body ?? '',
    status: (rawMessage?.status ?? 'SENT') as Message['status'],
    createdAt,
    updatedAt: rawMessage?.updatedAt ?? createdAt,
    attachments:
      Array.isArray(rawMessage?.attachments) && rawMessage.attachments.length > 0
        ? rawMessage.attachments
        : undefined,
  };
};

const extractSocketToken = (payload: any): string | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;

  if (typeof payload.token === 'string') {
    return payload.token;
  }

  if (typeof payload.wsToken === 'string') {
    return payload.wsToken;
  }

  if (payload.data && typeof payload.data === 'object') {
    const nested = payload.data;
    if (typeof nested.token === 'string') {
      return nested.token;
    }
    if (typeof nested.wsToken === 'string') {
      return nested.wsToken;
    }
  }

  return undefined;
};

export function EnhancedChatInterface({ conversationId, tailor, currentUserId }: ChatInterfaceProps) {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState<TypingIndicator[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [counterpartPresence, setCounterpartPresence] = useState<{
    is_online: boolean;
    is_active: boolean;
    last_seen: string | null;
    profile_completed: boolean;
  } | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTypingTimeRef = useRef<number>(0);

  const formatRoleLabel = (role?: string | null) => {
    if (!role) return 'Participant';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const counterpartName = tailor.name?.trim() || tailor.email || 'Conversation Partner';
  const counterpartEmail = tailor.email;
  const counterpartRoleLabel = formatRoleLabel(tailor.role);
  const counterpartInitial = counterpartName.charAt(0)?.toUpperCase() || 'U';

  const currentUserName = user?.name?.trim() || user?.email || 'You';
  const currentUserRoleLabel = formatRoleLabel(user?.role);

  const placeholderName =
    tailor.name?.trim() ||
    (tailor.role === 'tailor'
      ? 'your tailor'
      : tailor.role === 'customer'
        ? 'your customer'
        : 'your contact');

  const connectionStatusLabel = isConnected ? 'Connected' : 'Disconnected';
  const connectionStatusHint = isConnected
    ? 'You are connected to the chat server'
    : 'Trying to reconnect…';

  // Fetch counterpart presence status
  const fetchCounterpartPresence = useCallback(async () => {
    if (!tailor.id || !token) return;
    
    try {
      const response = await fetch(`${apiUrl}/api/chat/presence/${tailor.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const presence = await response.json();
        setCounterpartPresence(presence);
      }
    } catch (_error) {
      console.error('Failed to fetch counterpart presence:', error);
    }
  }, [tailor.id, token]);

  // Fetch presence when component mounts or tailor changes
  useEffect(() => {
    fetchCounterpartPresence();
  }, [fetchCounterpartPresence]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!conversationId || !user || !token) return;

    const initializeSocket = async () => {
      try {
        // Get WebSocket token
        const wsTokenResponse = await fetch(apiUrl('/api/chat/ws-token'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!wsTokenResponse.ok) {
          throw new Error('Failed to get WebSocket token');
        }

        const wsTokenPayload = await wsTokenResponse.json();
        const resolvedToken = extractSocketToken(wsTokenPayload) ?? token;
        const normalizedToken = resolvedToken?.toString().trim();

        if (!normalizedToken) {
          console.error('Chat socket: invalid token payload', wsTokenPayload);
          throw new Error('WebSocket token not available');
        }

        const connectionOptions: Partial<ManagerOptions & SocketOptions> = {
          transports: ['websocket', 'polling'],
          auth: { token: normalizedToken },
          query: { token: normalizedToken },
          reconnectionAttempts: 5,
          withCredentials: true
        };

        // Connect to Socket.IO server
        socketRef.current = io(SOCKET_URL, connectionOptions);

        const socket = socketRef.current;

        // Connection events
        socket.on('connect', () => {
          console.log('✅ Connected to chat server');
          setIsConnected(true);
          setError(null);
          
          // Join conversation room
          socket.emit('join_conversation', { conversationId });
        });

        socket.on('disconnect', () => {
          console.log('🔌 Disconnected from chat server');
          setIsConnected(false);
        });

        socket.io.on('reconnect_attempt', (attempt) => {
          console.warn(`Chat socket reconnect attempt ${attempt}`);
        });

        socket.io.on('reconnect_failed', () => {
          console.error('Chat socket reconnection failed');
        });

        socket.on('connect_error', (err: Error & { data?: unknown }) => {
          console.error('❌ Socket connection error:', err, err?.message, err?.data);
          const serverMessage =
            typeof err?.data === 'object' &&
            err?.data !== null &&
            'message' in (err.data as Record<string, unknown>)
              ? String((err.data as Record<string, unknown>).message)
              : err?.message;
          setError(serverMessage || 'Failed to connect to chat server');
        });

        socket.on('error', (err: unknown) => {
          console.error('🚨 Chat socket error event:', err);
        });

        // Listen for real-time presence updates
        socket.on('user_status_changed', (data: { userId: string; isOnline: boolean; conversationId: string }) => {
          console.log('📢 Received user status change:', data);
          
          // If this is for our counterpart, update their presence
          if (data.userId === tailor.id.toString()) {
            setCounterpartPresence(prev => prev ? {
              ...prev,
              is_online: data.isOnline
            } : null);
            
            console.log(`📢 ${tailor.name} is now ${data.isOnline ? 'online' : 'offline'}`);
          }
        });

        // Message events
        socket.on('msg:new@v1', (message: Message) => {
          const normalizedMessage = normalizeMessage(
            message,
            currentUserId,
            conversationId
          );

          setMessages(prev => {
            // Avoid duplicates
            if (prev.find(m => m.id === normalizedMessage.id)) return prev;
            return sortMessagesAscending([...prev, normalizedMessage]);
          });
          
          // Mark as read if it's not from current user
          if (normalizedMessage.senderId !== currentUserId.toString()) {
            socket.emit('mark_read', {
              conversationId,
              messageId: normalizedMessage.id
            });
          }
        });

        // Typing indicators
        socket.on('presence:typing@v1', (data: { userId: string; typing: boolean }) => {
          if (data.userId !== currentUserId.toString()) {
            setTypingUsers(prev => {
              const filtered = prev.filter(t => t.userId !== data.userId);
              if (data.typing) {
                return [...filtered, { userId: data.userId, isTyping: true }];
              }
              return filtered;
            });
          }
        });

        // Read receipts
        socket.on('reads:peer@v1', (data: { userId: string; messageId: string; readAt: string }) => {
          setMessages(prev => prev.map(msg => 
            msg.id === data.messageId && msg.senderId === currentUserId.toString()
              ? { ...msg, status: 'READ' as const }
              : msg
          ));
        });

        // User presence
        socket.on('user_joined', (data: { userId: string }) => {
          console.log(`👤 User ${data.userId} joined conversation`);
        });

        socket.on('user_left', (data: { userId: string }) => {
          console.log(`👤 User ${data.userId} left conversation`);
        });

      } catch (_err) {
        console.error('Failed to initialize WebSocket:', err);
        setError('Failed to connect to real-time chat');
      }
    };

    initializeSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_conversation', { conversationId });
        socketRef.current.disconnect();
      }
    };
  }, [conversationId, user, token, currentUserId]);

  // Fetch initial messages
  useEffect(() => {
    fetchMessages();
  }, [conversationId]);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages, typingUsers]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(apiUrl(`/api/chat/conversations/${conversationId}/messages`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch messages: ${response.status}`);
      }

      const data = await response.json();
      let rawMessages: any[] = [];
      
      if (Array.isArray(data)) {
        rawMessages = data;
      } else if (Array.isArray(data.items)) {
        rawMessages = data.items;
      } else if (Array.isArray(data.messages)) {
        rawMessages = data.messages;
      } else if (Array.isArray(data.data)) {
        rawMessages = data.data;
      }

      const normalizedMessages = sortMessagesAscending(
        rawMessages.map((msg: any) =>
          normalizeMessage(msg, currentUserId, conversationId)
        )
      );

      setMessages(normalizedMessages);
    } catch (_err) {
      console.error('Failed to fetch messages:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch messages');
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (attachments: Attachment[] = []) => {
    if ((!newMessage.trim() && attachments.length === 0) || isSending) return;

    try {
      setIsSending(true);
      setError(null);

      // Stop typing indicator
      handleTypingStop();

      const response = await fetch(apiUrl('/api/chat/messages'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversationId: conversationId,
          body: newMessage.trim(),
          attachments: attachments
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }

      const sentMessage = await response.json();
      const normalizedMessage = normalizeMessage(
        sentMessage,
        currentUserId,
        conversationId
      );

      setMessages(prev => {
        if (prev.find(m => m.id === normalizedMessage.id)) return prev;
        return sortMessagesAscending([...prev, normalizedMessage]);
      });

      setNewMessage('');
    } catch (_err) {
      console.error('Failed to send message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleFileUploaded = (attachment: Attachment) => {
    // Send message with attachment
    sendMessage([attachment]);
  };

  const handleTypingStart = useCallback(() => {
    if (!socketRef.current || isTyping) return;
    
    setIsTyping(true);
    socketRef.current.emit('typing_start', { conversationId });
    
    // Auto-stop typing after 3 seconds of inactivity
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(handleTypingStop, 3000);
  }, [conversationId, isTyping]);

  const handleTypingStop = useCallback(() => {
    if (!socketRef.current || !isTyping) return;
    
    setIsTyping(false);
    socketRef.current.emit('typing_stop', { conversationId });
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  }, [conversationId, isTyping]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    
    if (e.target.value.trim()) {
      handleTypingStart();
    } else {
      handleTypingStop();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusIcon = (status: string, isOwnMessage: boolean) => {
    if (!isOwnMessage) return null;
    
    switch (status) {
      case 'SENT':
        return <Check className="w-3 h-3 text-gray-400" />;
      case 'DELIVERED':
        return <CheckCheck className="w-3 h-3 text-gray-400" />;
      case 'READ':
        return <CheckCheck className="w-3 h-3 text-blue-500" />;
      default:
        return <Clock className="w-3 h-3 text-gray-400" />;
    }
  };

  const renderAttachment = (attachment: Attachment) => {
    if (attachment.mimeType.startsWith('image/')) {
      return (
        <div className="mt-2">
          <img
            src={apiUrl(attachment.url)}
            alt={attachment.name}
            className="max-w-xs rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => window.open(apiUrl(attachment.url), '_blank')}
          />
        </div>
      );
    } else {
      return (
        <div className="mt-2 flex items-center space-x-2 p-2 bg-gray-100 rounded-lg">
          <File className="w-4 h-4 text-gray-600" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{attachment.name}</p>
            <p className="text-xs text-gray-500">
              {(attachment.size / 1024).toFixed(1)} KB
            </p>
          </div>
          <button
            onClick={() => window.open(apiUrl(attachment.url), '_blank')}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
          >
            <Download className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-200px)] md:h-[600px] border border-gray-200 rounded-lg overflow-hidden bg-white flex flex-col">
      {/* Chat Header */}
      <div className="bg-white px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {counterpartInitial}
              </span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{counterpartName}</h3>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  counterpartPresence?.is_online 
                    ? 'bg-green-500' 
                    : counterpartPresence?.is_active 
                      ? 'bg-yellow-500' 
                      : 'bg-gray-400'
                }`}></div>
                <p className="text-sm text-gray-500">
                  {counterpartPresence?.is_online 
                    ? 'Online' 
                    : counterpartPresence?.is_active 
                      ? 'Active' 
                      : 'Offline'}
                  {!counterpartPresence?.profile_completed && ' (Unverified)'}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span className="text-sm text-gray-600">You are online</span>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 p-6 overflow-y-auto bg-gray-50">
        <div className="space-y-4">
          {messages.length === 0 ? (
            <div className="flex justify-center">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">
                      {counterpartInitial}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-900">Welcome to your chat!</p>
                    <p className="text-xs text-blue-700 mt-1">
                      You can now start messaging with {placeholderName}. 
                      Send a message below to get started.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.senderId === currentUserId.toString() ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    message.senderId === currentUserId.toString()
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-900 border border-gray-200'
                  }`}
                >
                  {message.body && <p className="text-sm">{message.body}</p>}
                  {message.attachments && message.attachments.map((attachment, idx) => (
                    <div key={idx}>
                      {renderAttachment(attachment)}
                    </div>
                  ))}
                  <div className="flex items-center justify-between mt-1">
                    <p className={`text-xs ${
                      message.senderId === currentUserId.toString() ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {formatTime(message.createdAt)}
                    </p>
                    {getStatusIcon(message.status, message.senderId === currentUserId.toString())}
                  </div>
                </div>
              </div>
            ))
          )}
          
          {/* Typing Indicators */}
          {typingUsers.length > 0 && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 max-w-xs">
                <div className="flex items-center space-x-1">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-xs text-gray-500 ml-2">
                    {counterpartName} is typing...
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="px-6 py-2 bg-red-50 border-t border-red-200">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Message Input */}
      <div className="border-t border-gray-200 p-3 md:p-4 bg-white">
        <div className="flex items-center space-x-2 md:space-x-3">
          <FileUpload
            conversationId={conversationId}
            onFileUploaded={handleFileUploaded}
            disabled={isSending}
          />
          <div className="flex-1 relative">
            <input
              type="text"
              value={newMessage}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder={`Type a message to ${placeholderName}...`}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
              disabled={isSending}
            />
            <button className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors">
              <Smile className="w-4 h-4 text-gray-500" />
            </button>
          </div>
          <button
            onClick={() => sendMessage()}
            disabled={!newMessage.trim() || isSending}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
          >
            {isSending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Send</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
